import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search, AlertCircle, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UniversalAddressInput = ({ 
  value, 
  onChange,
  onAddressSelect, // Callback with full address data: {formatted_address, lat, lng, place_id, manual_entry}
  placeholder = "Enter Australian address...",
  className = "jop-input",
  label = "Address",
  required = false,
  showCoordinates = false,
  showPlaceId = false
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionToken] = useState(() => `session_${Date.now()}_${Math.random()}`);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  const searchPlaces = async (query) => {
    if (!query.trim() || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/places/autocomplete`, {
        params: {
          q: query,
          session: sessionToken
        }
      });

      if (response.data.status === 'OK' && response.data.predictions) {
        // Limit to top 5 suggestions as per requirements
        const topSuggestions = response.data.predictions.slice(0, 5);
        setSuggestions(topSuggestions);
        setShowSuggestions(true);
      } else if (response.data.status === 'ZERO_RESULTS') {
        setSuggestions([]);
        setShowSuggestions(false);
      } else {
        console.error('Places API status:', response.data.status);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Places API error:', error);
      
      if (error.response?.status === 429) {
        setError('Too many requests - please wait');
        toast.error('Address search limit reached - please wait a moment');
      } else {
        setError('Address lookup failed');
      }
      
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    // Clear selection when user types
    if (selectedAddress && selectedAddress.formatted_address !== inputValue) {
      setSelectedAddress(null);
      // Mark as manual entry
      if (onAddressSelect) {
        onAddressSelect({
          formatted_address: inputValue,
          lat: null,
          lng: null,
          place_id: null,
          manual_entry: true
        });
      }
    }

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce API calls (300ms as per requirements)
    if (inputValue.length >= 3) {
      timeoutRef.current = setTimeout(() => {
        searchPlaces(inputValue);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setError(null);
    }
  };

  const handleSuggestionClick = async (prediction) => {
    onChange(prediction.description);
    setShowSuggestions(false);
    setLoading(true);

    try {
      // Get detailed place information
      const response = await axios.get(`${API}/places/details`, {
        params: {
          place_id: prediction.place_id,
          session: sessionToken
        }
      });

      if (response.data.status === 'OK' && response.data.result) {
        const place = response.data.result;
        
        const addressData = {
          formatted_address: place.formatted_address,
          lat: place.geometry?.location?.lat,
          lng: place.geometry?.location?.lng,
          place_id: place.place_id,
          manual_entry: false
        };

        setSelectedAddress(addressData);
        
        // Callback with complete address data
        if (onAddressSelect) {
          onAddressSelect(addressData);
        }

        toast.success('Address validated with Google Places');
      } else {
        console.error('Place details error:', response.data.status);
        toast.error('Failed to get address details');
      }
    } catch (error) {
      console.error('Error getting place details:', error);
      toast.error('Failed to validate address');
    } finally {
      setLoading(false);
    }
  };

  const handleClickOutside = (e) => {
    if (inputRef.current && !inputRef.current.contains(e.target)) {
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative" ref={inputRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={`${className} ${selectedAddress && !selectedAddress.manual_entry ? 'border-green-300 bg-green-50' : ''}`}
          placeholder={placeholder}
          autoComplete="off"
          required={required}
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          ) : error ? (
            <AlertCircle className="h-4 w-4 text-red-400" />
          ) : selectedAddress && !selectedAddress.manual_entry ? (
            <div className="flex items-center">
              <MapPin className="h-4 w-4 text-green-500" />
            </div>
          ) : (
            <Search className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {suggestions.map((prediction) => (
            <button
              key={prediction.place_id}
              onClick={() => handleSuggestionClick(prediction)}
              className="w-full px-4 py-3 text-left hover:bg-gray-100 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-gray-100"
            >
              <div className="flex items-start">
                <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {prediction.structured_formatting?.main_text || prediction.description}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {prediction.structured_formatting?.secondary_text || ''}
                  </div>
                </div>
              </div>
            </button>
          ))}
          
          {/* Google Attribution - Required */}
          <div className="px-4 py-2 bg-gray-50 border-t text-right">
            <span className="text-xs text-gray-500">Powered by Google</span>
          </div>
        </div>
      )}

      {/* Status Messages */}
      <div className="mt-1 flex justify-between items-center text-xs">
        <div>
          {error ? (
            <span className="text-red-600 flex items-center">
              <AlertCircle className="h-3 w-3 mr-1" />
              {error}
            </span>
          ) : selectedAddress && !selectedAddress.manual_entry ? (
            <span className="text-green-600 flex items-center">
              <MapPin className="h-3 w-3 mr-1" />
              Address verified with Google Places
            </span>
          ) : selectedAddress && selectedAddress.manual_entry ? (
            <span className="text-amber-600 flex items-center">
              <AlertCircle className="h-3 w-3 mr-1" />
              Manual entry - admin validation may be required
            </span>
          ) : (
            <span className="text-blue-600">
              {value && value.length >= 3 ? 
                (suggestions.length > 0 ? 
                  `${suggestions.length} Australian address${suggestions.length === 1 ? '' : 'es'} found` :
                  loading ? 'Searching Australian addresses...' : 'No matches - manual entry allowed'
                ) :
                'Type 3+ characters for Australian address suggestions'
              }
            </span>
          )}
        </div>
      </div>

      {/* Debug Information (only show if requested) */}
      {(showCoordinates || showPlaceId) && selectedAddress && (
        <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
          {showCoordinates && selectedAddress.lat && selectedAddress.lng && (
            <div className="text-gray-600">
              📍 Coordinates: {selectedAddress.lat}, {selectedAddress.lng}
            </div>
          )}
          {showPlaceId && selectedAddress.place_id && (
            <div className="text-gray-600">
              🆔 Place ID: {selectedAddress.place_id}
            </div>
          )}
          <div className="text-gray-600">
            ✓ Source: {selectedAddress.manual_entry ? 'Manual Entry' : 'Google Places'}
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversalAddressInput;